import React, { useEffect } from 'react';

const sections = [
  {
    title: '1. Introduction',
    content: [
      'CareersPOV is a career exploration and experiential learning platform operated by Giggly Gains, a partnership firm based in Pune, Maharashtra, India.',
      'CareersPOV helps school students explore careers, understand educational pathways and participate in career-related experiences through their school communities.',
      'We recognize the importance of protecting personal information, particularly information relating to children under 18 years of age.',
      'This Privacy Policy explains how Giggly Gains collects, receives, uses, stores, shares, protects, retains and otherwise processes personal information through CareersPOV.',
      'This Policy applies to:',
    ],
    bullets: [
      'Visitors to the CareersPOV website.',
      'Parents and legal guardians.',
      'Children whose profiles are created on CareersPOV.',
      'School coordinators and authorized school representatives.',
      'Parent professionals and approved mentors.',
      'Representatives of participating organizations.',
      'Individuals contacting CareersPOV for enquiries or support.',
    ],
    afterBullets: [
      'This Privacy Policy should be read together with the CareersPOV Terms of Use and applicable Student Safety & Experience Guidelines.',
    ],
  },
  {
    title: '2. Platform Operator and Data Protection Responsibilities',
    content: [
      'CareersPOV is owned and operated by:',
    ],
    contact: true,
    afterContact: [
      'CareersPOV is a technology platform that enables participating schools to facilitate career exploration and experiential learning opportunities for their students.',
      'Participating schools are responsible for administering their school communities, including student and parent information, school approvals, mentor participation and school-organized career experiences.',
      'Schools are the primary point of contact for parents and legal guardians regarding student information and participation.',
      'Giggly Gains provides the technology infrastructure and associated platform services.',
      'The respective data protection responsibilities of Giggly Gains and participating schools depend on their roles in determining the purposes and means of processing personal information.',
      'Where Giggly Gains processes personal information on behalf of a participating school, the relevant responsibilities may also be governed by a separate agreement.',
      'Giggly Gains remains responsible for its own processing activities and obligations under applicable law.',
      'Nothing in this Policy removes any statutory obligation applicable to Giggly Gains, participating schools or other parties.',
    ],
  },
  {
    title: '3. How CareersPOV Accounts Work',
    content: [
      'CareersPOV currently operates through a parent-mediated account model.',
      'Parents register using a participating school\u2019s invitation code and may create profiles for their children.',
      'Children do not currently have independent login accounts.',
      'Each child profile is associated with a parent account and a participating school.',
      'School coordinators review child profiles and administer approved school activities.',
      'Parent professionals may apply to become mentors. Their participation is subject to approval by the relevant school coordinator.',
      'CareersPOV uses role-based access controls to restrict information according to authorized roles and school affiliation.',
      'Platform administrators may access information across schools where reasonably necessary for authorized administration, technical support, security and platform operations.',
    ],
  },
  {
    title: '4. Personal Information We Collect',
    content: [
      'We collect personal information reasonably necessary to provide and operate CareersPOV.',
    ],
    subsections: [
      {
        subtitle: '4.1 Parent and Legal Guardian Information',
        intro: 'This may include:',
        bullets: [
          'Full name.',
          'Email address.',
          'Mobile number.',
          'School affiliation.',
          'Account identifiers and authentication information.',
          'Information linking parents to child profiles.',
          'Experience bookings and parental confirmations.',
          'Relevant communications, requests and feedback.',
        ],
      },
      {
        subtitle: '4.2 Children\u2019s Information',
        intro: 'Parents may provide information about their children, including:',
        bullets: [
          'Full name.',
          'School affiliation.',
          'Grade and division.',
          'School admission number.',
          'Career interests and preferences.',
          'Profile notes.',
          'Saved career information.',
          'Experience bookings and participation records.',
          'Feedback and certificate-related information.',
        ],
        after: [
          'Children do not currently need independent email addresses or phone numbers to have profiles on CareersPOV.',
          'Parents should avoid including unnecessary confidential, medical or other sensitive information in optional profile fields.',
        ],
      },
      {
        subtitle: '4.3 Mentor Information',
        intro: 'Mentor information may include:',
        bullets: [
          'Full name and contact information.',
          'School affiliation.',
          'Professional title and employer.',
          'Professional biography and experience.',
          'Career expertise.',
          'Professional profile links.',
          'Preferred location and availability.',
          'Experience offerings.',
          'Mentor application and approval status.',
          'Participation and feedback information.',
        ],
      },
      {
        subtitle: '4.4 School Coordinator Information',
        intro: 'We may collect:',
        bullets: [
          'Name and official contact information.',
          'School affiliation.',
          'Administrative role.',
          'Account and authentication information.',
          'Records associated with authorized school administration.',
        ],
      },
      {
        subtitle: '4.5 School and Business Enquiries',
        intro: 'Individuals contacting CareersPOV may provide:',
        bullets: [
          'Name.',
          'Email address.',
          'Phone number.',
          'School or organization name.',
          'School website.',
          'Enquiry details.',
        ],
      },
      {
        subtitle: '4.6 Technical and Security Information',
        intro: 'We and our service providers may process technical information such as:',
        bullets: [
          'IP addresses.',
          'Authentication and session identifiers.',
          'Login-attempt information.',
          'Browser and device information.',
          'Security and diagnostic information.',
          'Cookies and functional session-storage information.',
        ],
        after: [
          'The precise technical information processed depends on the application and infrastructure used.',
          'CareersPOV does not ordinarily require children to provide government identification numbers, financial information or medical records for career exploration.',
        ],
      },
    ],
  },
  {
    title: '5. How We Collect Personal Information',
    content: [
      'We may collect or receive information:',
    ],
    bullets: [
      'Directly from parents and legal guardians.',
      'When parents create or update child profiles.',
      'From school coordinators performing authorized activities.',
      'From mentors registering or offering experiences.',
      'Through booking requests and participation confirmations.',
      'Through feedback, enquiries and support communications.',
      'Automatically through authentication and technical systems.',
      'From participating schools where they are authorized to provide the information.',
    ],
    afterBullets: [
      'Where participating schools provide personal information, they must comply with applicable collection, notice, consent and disclosure requirements.',
      'Giggly Gains remains responsible for the lawful processing of information it receives and uses.',
    ],
  },
  {
    title: '6. How We Use Personal Information',
    content: [
      'We process personal information for the following purposes:',
    ],
    bullets: [
      'Creating and managing parent, mentor and school coordinator accounts.',
      'Associating parents and child profiles with participating schools.',
      'Verifying and administering school affiliation.',
      'Managing child-profile approvals.',
      'Providing career exploration and educational information.',
      'Saving career interests and preferences.',
      'Managing mentor applications and school approvals.',
      'Displaying school-specific career experiences.',
      'Processing bookings, mentor assignments and parental confirmations.',
      'Coordinating online sessions and workplace visits.',
      'Sending transactional notifications and reminders.',
      'Managing participation records, feedback and certificates.',
      'Responding to enquiries and support requests.',
      'Protecting platform security and preventing misuse.',
      'Investigating safety, privacy or security concerns.',
      'Complying with applicable legal obligations.',
    ],
    afterBullets: [
      'We do not use children\u2019s personal information for targeted advertising or commercial profiling.',
      'We will not process personal information for unrelated purposes without an appropriate lawful basis and any additional notice or consent required by applicable law.',
    ],
  },
  {
    title: '7. Legal Basis, Notice and Consent',
    content: [
      'We process personal information only where permitted under applicable law.',
      'Depending on the circumstances and the legal provisions in force, processing may be based on valid consent, a legally recognized permitted use or another applicable authorization.',
      'Where consent is required, the relevant notice and consent request will identify the personal information involved, the specified purposes of processing and the means of exercising applicable rights.',
      'Acceptance of the Terms of Use, account registration, school approval and parental confirmation of a career experience are not automatically interchangeable forms of consent.',
      'Where consent is legally required for a particular purpose, an unrelated approval will not be treated as a substitute.',
      'Additional notices or authorization may be required for particular activities or new uses of personal information.',
    ],
  },
  {
    title: '8. Children\u2019s Privacy and Parental Authorization',
    content: [
      'CareersPOV is intended primarily for school students, including children under 18 years of age.',
      'We recognize that children\u2019s personal information requires additional protection.',
      'Under the current platform model, parents create and manage child profiles.',
      'Where applicable law requires verifiable parental or lawful guardian consent, personal information must be processed in accordance with that requirement, subject to any legally applicable exception.',
      'The current parent-mediated registration process is distinct from a separately recorded, versioned consent mechanism for processing children\u2019s personal information.',
      'The absence of an automated consent feature does not remove any applicable legal obligation to obtain valid consent.',
      'CareersPOV does not currently use children\u2019s personal information for targeted advertising or behavioural tracking.',
      'We do not sell children\u2019s personal information.',
      'We will not knowingly undertake processing of children\u2019s personal information prohibited by applicable law.',
    ],
  },
  {
    title: '9. Parental Approval for Career Experiences',
    content: [
      'Parents may request career experiences for their children through CareersPOV.',
      'Following the applicable mentor assignment process, the parent must confirm participation in the relevant experience.',
      'This confirmation is recorded as part of the booking process.',
      'Parental confirmation authorizes participation in the specified activity, subject to applicable school requirements.',
      'It does not replace any separate consent required for processing personal information.',
      'Additional permissions may be necessary for:',
    ],
    bullets: [
      'Workplace visits.',
      'Job shadowing.',
      'Internships.',
      'Activities involving travel or external locations.',
      'Photography, video recording or publication of student information.',
      'Other activities requiring additional safeguards.',
    ],
    afterBullets: [
      'Parents should review the relevant experience details before authorizing participation.',
    ],
  },
  {
    title: '10. School-Specific Access and Data Protection',
    content: [
      'CareersPOV operates through participating school communities.',
      'The platform uses school affiliation and role-based authorization to restrict access to student, mentor and experience information.',
      'Parents may access information associated with their linked child profiles.',
      'School coordinators may access information necessary to administer their own school communities.',
      'Approved mentors may access information required to conduct authorized experiences.',
      'Platform administrators may access information across schools where necessary for legitimate platform responsibilities.',
      'CareersPOV does not currently provide general cross-school sharing of student and mentor communities.',
      'We use backend authorization controls to support these restrictions and periodically review their effectiveness.',
      'No access-control system can guarantee absolute protection against unauthorized access.',
    ],
  },
  {
    title: '11. Information Shared With Mentors',
    content: [
      'When a mentor is assigned to an experience, CareersPOV may make relevant booking information available.',
      'Depending on the booking, this may include:',
    ],
    bullets: [
      'Child\u2019s name.',
      'Grade and division.',
      'School admission number.',
      'Parent\u2019s name.',
      'Career or experience details.',
      'Scheduled date and time.',
      'Relevant participation information.',
    ],
    afterBullets: [
      'The current platform does not ordinarily expose student or parent email addresses and mobile numbers to mentors through booking records.',
      'Mentors must not:',
    ],
    bullets2: [
      'Use student information for unrelated purposes.',
      'Share student records with unauthorized individuals.',
      'Contact students privately outside authorized arrangements.',
      'Use student information for commercial solicitation.',
      'Publish student names, photographs or recordings without required authorization.',
      'Retain or reuse student information beyond an authorized purpose.',
    ],
    afterBullets2: [
      'Additional information sharing for particular activities must be subject to appropriate authorization and safeguards.',
    ],
  },
  {
    title: '12. Sharing Information With Schools and Other Parties',
    content: [
      'Personal information may be shared with authorized parties where reasonably necessary for CareersPOV\u2019s services.',
      'These may include:',
    ],
    bullets: [
      'Participating schools: For school administration, mentor approval, experience oversight and safeguarding.',
      'Parents and guardians: For managing linked child profiles and participation.',
      'Approved mentors: For conducting authorized experiences.',
      'Host organizations: Where necessary for approved workplace visits, internships or similar activities.',
      'Technology service providers: For hosting, storage, authentication, email and other operational functions.',
      'Competent authorities: Where disclosure is required by law or legally necessary for an appropriate safety or security response.',
    ],
    afterBullets: [
      'Information sharing must be limited to appropriate purposes and comply with applicable legal requirements.',
      'We do not sell personal information to advertisers, data brokers or other third parties.',
    ],
  },
  {
    title: '13. Third-Party Technology Providers',
    content: [
      'CareersPOV currently uses the following technologies and service providers:',
    ],
    bullets: [
      'Emergent Platform: Application hosting and supporting infrastructure.',
      'MongoDB: Database technology used for platform information.',
      'Resend: Transactional email delivery.',
      'Emergent Object Storage: Storage of platform assets, including images and school logos.',
      'ReportLab: Software used within the application to generate participation certificates.',
    ],
    afterBullets: [
      'These technologies may process information necessary for their respective functions.',
      'The use of a software library does not necessarily mean its developer receives personal information. Actual data processing depends on the relevant hosting and deployment arrangements.',
      'We seek to limit access by service providers to information necessary for authorized services and apply appropriate contractual and security safeguards.',
      'Some infrastructure or service providers may process information outside India.',
      'Any international processing or transfer will be subject to applicable legal requirements.',
    ],
  },
  {
    title: '14. Online Career Experiences',
    content: [
      'CareersPOV may facilitate online career experiences using meeting links or external video-conferencing services.',
      'The platform currently supports manually provided session links.',
      'External conferencing providers may independently process information under their own privacy policies.',
      'Mentors and participants must not record, distribute or publish sessions involving children without the required authorization.',
      'Where recording or additional processing is proposed, appropriate notice, consent and safeguarding arrangements must be established.',
    ],
  },
  {
    title: '15. Workplace Visits, Job Shadowing and Internships',
    content: [
      'Some CareersPOV experiences may take place outside the online platform.',
      'These may involve professional workplaces, workshops, job shadowing or internships.',
      'Additional information may be required to arrange participation, including:',
    ],
    bullets: [
      'Location and schedule.',
      'Host organization.',
      'Responsible contact person.',
      'Parent or guardian authorization.',
      'Attendance information.',
      'Relevant safety and emergency arrangements.',
    ],
    afterBullets: [
      'Information must be limited to what is reasonably necessary for the activity.',
      'Schools, mentors and host organizations must protect student information and comply with applicable child-protection, privacy and other legal requirements.',
      'Student information must not be used for unrelated recruitment, marketing or personal purposes without appropriate legal authorization.',
      'Additional notices and permissions may apply to particular activities.',
    ],
  },
  {
    title: '16. Cookies and Similar Technologies',
    content: [
      'CareersPOV currently uses essential authentication cookies and functional session storage.',
      'Authentication cookies include:',
    ],
    bullets: [
      'access_token: Supports authenticated access.',
      'refresh_token: Supports continued authorized sessions.',
    ],
    afterBullets: [
      'Functional session storage may preserve career exploration filters and questionnaire progress.',
      'CareersPOV does not currently use:',
    ],
    bullets2: [
      'Google Analytics.',
      'Advertising pixels.',
      'Third-party behavioural tracking scripts.',
      'Targeted advertising technologies.',
    ],
    afterBullets2: [
      'No optional advertising or analytics cookie-consent banner is currently implemented.',
      'If non-essential tracking technologies are introduced, we will review the relevant notice and consent requirements before using them.',
      'Users may manage certain browser storage settings, although disabling essential functionality may affect access to the platform.',
    ],
  },
  {
    title: '17. Communications and Notifications',
    content: [
      'CareersPOV may send communications relating to:',
    ],
    bullets: [
      'Registration and account access.',
      'Password recovery.',
      'Mentor applications and approvals.',
      'Booking requests.',
      'Mentor assignments.',
      'Parental confirmations.',
      'Session details and reminders.',
      'Feedback requests.',
      'Participation updates.',
      'School enquiries.',
      'Security and policy notices.',
    ],
    afterBullets: [
      'Transactional emails are processed through Resend.',
      'Communications may contain information necessary to identify the relevant account, child profile, booking or experience.',
      'We aim to limit the personal information included in notifications to what is reasonably necessary.',
      'CareersPOV does not currently send promotional marketing emails through its platform notification system.',
      'Any future marketing communications will be subject to applicable notice, consent and communication requirements.',
    ],
  },
  {
    title: '18. Data Security',
    content: [
      'Giggly Gains uses technical and organizational safeguards intended to protect personal information.',
      'Current platform controls include:',
    ],
    bullets: [
      'Password hashing.',
      'Authentication and session management.',
      'Secure authentication cookies for HTTPS deployments.',
      'Role-based access restrictions.',
      'School-specific backend authorization.',
      'Login-attempt protection.',
      'Restricted access to child contact information.',
      'School coordinator approval of mentors.',
      'Parental confirmation of experience bookings.',
    ],
    afterBullets: [
      'We continue to assess security requirements as the platform develops.',
      'No internet-based service can guarantee absolute security.',
      'Users should protect their credentials and promptly report suspected unauthorized access to contact@gigglygains.com.',
    ],
  },
  {
    title: '19. Personal-Data Breaches and Security Incidents',
    content: [
      'If Giggly Gains becomes aware of a suspected personal-data breach, we will assess the nature, scope and potential impact of the incident.',
      'Appropriate action may include:',
    ],
    bullets: [
      'Investigating the incident.',
      'Restricting unauthorized access.',
      'Taking reasonable mitigation measures.',
      'Preserving relevant records where lawful.',
      'Notifying affected individuals where required.',
      'Notifying competent authorities where required.',
      'Cooperating with participating schools and relevant service providers.',
    ],
    afterBullets: [
      'Notifications will be made in accordance with applicable legal requirements and timelines.',
      'Where an incident involves a child\u2019s safety, additional safeguarding and mandatory reporting obligations may apply.',
    ],
  },
  {
    title: '20. Data Retention',
    content: [
      'We retain personal information for purposes permitted by applicable law, taking account of the purposes for which it was collected and any applicable retention or erasure requirements.',
      'Relevant considerations may include:',
    ],
    bullets: [
      'Continued account activity.',
      'Participation in a school community.',
      'Outstanding bookings or experiences.',
      'Operational requirements.',
      'Legal and regulatory obligations.',
      'Safeguarding concerns.',
      'Security investigations.',
      'Dispute resolution.',
    ],
    afterBullets: [
      'CareersPOV does not currently provide automated account deletion or a comprehensive automated retention system.',
      'Requests requiring deletion or other action are assessed individually through the applicable administrative process.',
      'Personal information will be erased when required by applicable law, subject to any lawful retention requirements.',
      'Certain information may need to be retained after an account becomes inactive where legally necessary.',
    ],
  },
  {
    title: '21. Correction, Updating and Erasure',
    content: [
      'Parents and legal guardians should contact their respective school\u2019s designated CareersPOV coordinator for requests relating to their child\u2019s personal information.',
      'These requests may include:',
    ],
    bullets: [
      'Correcting inaccurate student information.',
      'Updating student profiles.',
      'Requesting access to student information.',
      'Requesting removal of a student from CareersPOV.',
      'Requesting deletion or erasure of student information.',
    ],
    afterBullets: [
      'The school coordinator will review the request, verify the requester\u2019s authority where appropriate, and coordinate any required action.',
      'CareersPOV provides the technology functionality and technical assistance necessary to support authorized requests.',
      'Where a request requires backend deletion, anonymization or other technical intervention, the school coordinator may coordinate with Giggly Gains.',
      'The platform does not currently provide a self-service account-deletion feature.',
      'Requests will be handled in accordance with applicable legal requirements, including any lawful retention obligations.',
      'For requests concerning personal information processed independently by Giggly Gains, individuals may contact contact@gigglygains.com directly.',
      'Nothing in this process restricts an individual\u2019s rights under applicable data protection law.',
    ],
  },
  {
    title: '22. Withdrawal of Consent',
    content: [
      'Where processing is based on consent, individuals may withdraw that consent in accordance with applicable law.',
      'Parents and legal guardians wishing to withdraw consent relating to their child\u2019s participation or personal information should contact their school\u2019s designated CareersPOV coordinator.',
      'The school coordinator will review the request and coordinate the necessary actions.',
      'Where applicable, this may include:',
    ],
    bullets: [
      'Discontinuing the child\u2019s participation in CareersPOV.',
      'Restricting access to the child\u2019s profile.',
      'Cancelling pending experiences or bookings.',
      'Coordinating deletion or anonymization of personal information where legally required.',
    ],
    afterBullets: [
      'CareersPOV will provide the technical support necessary to implement authorized actions.',
      'The platform does not currently provide an automated consent-withdrawal feature.',
      'Withdrawal may affect the availability of services that depend on the relevant processing.',
      'Withdrawal does not necessarily affect processing lawfully completed before withdrawal or information that must be retained under applicable law.',
      'For consent relating to processing independently undertaken by Giggly Gains, individuals may contact contact@gigglygains.com directly.',
      'Nothing in this section limits the right to withdraw consent under applicable law.',
    ],
  },
  {
    title: '23. Privacy Rights and Grievance Redressal',
    content: [
      'Subject to applicable law and the commencement of relevant statutory provisions, individuals may have rights to:',
    ],
    bullets: [
      'Obtain information about the processing of their personal information.',
      'Request correction or updating.',
      'Request erasure.',
      'Withdraw consent.',
      'Raise privacy-related grievances.',
      'Exercise other rights available under applicable law.',
    ],
    afterBullets: [
      'Parents and legal guardians may exercise applicable rights on behalf of their children.',
      'For matters concerning student information, school records, student participation or school-managed career experiences, parents should first contact their school\u2019s designated CareersPOV coordinator.',
      'The school coordinator will act as the primary contact for receiving and coordinating such requests.',
      'Where technical assistance is required, the school may coordinate with Giggly Gains.',
      'For matters specifically concerning the CareersPOV platform, including platform security incidents, unauthorized platform access or personal information processed independently by Giggly Gains, individuals may contact: contact@gigglygains.com',
      'Giggly Gains will assess and respond to requests falling within its responsibilities in accordance with applicable law.',
      'Requests may require reasonable verification of identity or authority.',
      'Individuals may also contact Giggly Gains directly where required to exercise their legal rights, including where a school-level process is unavailable or has not adequately addressed the concern.',
      'Where applicable law provides a right to approach a competent regulatory authority, nothing in this Policy restricts that right.',
    ],
  },
  {
    title: '24. Children\u2019s Safety and Reporting Concerns',
    content: [
      'Users should promptly report concerns involving:',
    ],
    bullets: [
      'Unauthorized access to children\u2019s information.',
      'Inappropriate disclosure of student records.',
      'Unauthorized mentor contact.',
      'Harassment or exploitation.',
      'Misuse of student photographs or recordings.',
      'Unsafe or inappropriate interactions.',
      'Suspected privacy or security incidents.',
    ],
    afterBullets: [
      'Concerns relating to school-organized activities should also be reported to the relevant school coordinator.',
      'Platform-related concerns may be submitted to: contact@gigglygains.com',
      'Giggly Gains will review reported concerns and take appropriate action within its responsibilities.',
      'Where legally required, matters may be reported to competent authorities.',
      'Nothing in this Policy limits mandatory child-protection reporting obligations.',
    ],
  },
  {
    title: '25. International Data Processing and Transfers',
    content: [
      'CareersPOV may use infrastructure or service providers that process information outside India.',
      'Where personal information is transferred or processed internationally, Giggly Gains will comply with applicable Indian legal requirements and restrictions.',
      'Relevant service-provider arrangements and safeguards will be considered as required by applicable law.',
    ],
  },
  {
    title: '26. External Websites and Third-Party Resources',
    content: [
      'CareersPOV may provide links to:',
    ],
    bullets: [
      'Educational institutions.',
      'Universities and colleges.',
      'Professional organizations.',
      'Career resources.',
      'External meeting services.',
      'Other relevant websites.',
    ],
    afterBullets: [
      'These services may have independent privacy practices.',
      'Users should review the relevant third-party privacy policies before providing personal information.',
      'Giggly Gains is not responsible for independent third-party practices except to the extent required by applicable law.',
    ],
  },
  {
    title: '27. Changes to This Privacy Policy',
    content: [
      'We may update this Privacy Policy to reflect changes in:',
    ],
    bullets: [
      'Platform functionality.',
      'Information collection or processing.',
      'Service providers.',
      'Security and privacy practices.',
      'Applicable legal requirements.',
    ],
    afterBullets: [
      'Updated versions will be published on the CareersPOV website.',
      'The effective date or last-updated date will be revised as appropriate.',
      'Material changes will be communicated where required by applicable law.',
      'Where additional consent is legally required, it will be obtained before the relevant processing.',
    ],
  },
  {
    title: '28. Contact Information',
    content: [],
    customRender: true,
  },
];

function EmailLink({ text }) {
  const parts = text.split('contact@gigglygains.com');
  if (parts.length === 1) return text;
  return parts.map((part, i, arr) => (
    <React.Fragment key={i}>
      {part}
      {i < arr.length - 1 && (
        <a href="mailto:contact@gigglygains.com" className="text-[#F97D6A] hover:underline">contact@gigglygains.com</a>
      )}
    </React.Fragment>
  ));
}

function BulletList({ items }) {
  return (
    <ul className="mt-3 space-y-1.5 ml-1">
      {items.map((b, i) => (
        <li key={i} className="flex gap-2.5 text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
          <span className="mt-[10px] h-1.5 w-1.5 rounded-full bg-[#D1D5DB] shrink-0" />
          <span><EmailLink text={b} /></span>
        </li>
      ))}
    </ul>
  );
}

function Paragraphs({ items }) {
  return (
    <div className="mt-3 space-y-3">
      {items.map((p, i) => (
        <p key={i} className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
          <EmailLink text={p} />
        </p>
      ))}
    </div>
  );
}

function ContactBlock() {
  return (
    <div className="mt-3 bg-gray-50 rounded-xl p-5 sm:p-6 text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
      <p className="font-semibold text-[#1F2937]">Giggly Gains</p>
      <p>Indian Partnership Firm</p>
      <p className="mt-3 font-semibold text-[#1F2937]">Business Address:</p>
      <p>R103, Ganga Platino</p>
      <p>Near EON IT Park, Kharadi</p>
      <p>Pune, Maharashtra 411014</p>
      <p>India</p>
      <p className="mt-3">
        Email: <a href="mailto:contact@gigglygains.com" className="text-[#F97D6A] hover:underline">contact@gigglygains.com</a>
      </p>
    </div>
  );
}

export default function PrivacyPolicyPage() {
  useEffect(() => {
    document.title = 'Privacy Policy | CareersPOV';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Learn how CareersPOV, operated by Giggly Gains, collects, uses, protects, and manages personal information relating to parents, students, schools, and professional mentors.');
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="py-8 sm:py-12" data-testid="privacy-policy-page">
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <header className="mb-10 border-b border-gray-200 pb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1F2937] font-['Outfit'] mb-2">
            Privacy Policy
          </h1>
          <p className="text-sm text-[#6B7280] mb-1">
            CareersPOV &nbsp;|&nbsp; Operated by Giggly Gains
          </p>
          <p className="text-xs text-[#9CA3AF]">
            Effective Date: 9 October 2026 &nbsp;&middot;&nbsp; Last Updated: 9 October 2026
          </p>
        </header>

        {/* Sections */}
        <div className="space-y-8">
          {sections.map((section, idx) => (
            <section key={idx} data-testid={`privacy-section-${idx + 1}`}>
              <h2 className="text-base sm:text-lg font-bold text-[#1F2937] font-['Outfit'] mb-3">
                {section.title}
              </h2>

              {section.content && (
                <div className="space-y-3">
                  {section.content.map((p, i) => (
                    <p key={i} className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">
                      <EmailLink text={p} />
                    </p>
                  ))}
                </div>
              )}

              {section.contact && <ContactBlock />}
              {section.contactFinal && <ContactBlock />}

              {section.afterContact && <Paragraphs items={section.afterContact} />}

              {section.subsections && section.subsections.map((sub, si) => (
                <div key={si} className="mt-5">
                  <h3 className="text-sm sm:text-base font-semibold text-[#1F2937] font-['Outfit'] mb-2">{sub.subtitle}</h3>
                  <p className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">{sub.intro}</p>
                  <BulletList items={sub.bullets} />
                  {sub.after && <Paragraphs items={sub.after} />}
                </div>
              ))}

              {section.bullets && <BulletList items={section.bullets} />}
              {section.afterBullets && <Paragraphs items={section.afterBullets} />}
              {section.bullets2 && <BulletList items={section.bullets2} />}
              {section.afterBullets2 && <Paragraphs items={section.afterBullets2} />}

              {section.customRender && section.title.startsWith('28.') && (
                <>
                  <h3 className="text-sm sm:text-base font-semibold text-[#1F2937] font-['Outfit'] mt-4 mb-2">Student Information and School-Related Requests</h3>
                  <p className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8]">For requests concerning student information, including:</p>
                  <BulletList items={[
                    'Student profile corrections.',
                    'Student participation.',
                    'Consent-related requests.',
                    'Withdrawal of participation.',
                    'Student information access.',
                    'Student profile removal or deletion.',
                    'School-managed career experiences.',
                    'Concerns involving school-approved mentors.',
                  ]} />
                  <Paragraphs items={[
                    'Parents and legal guardians should contact their respective school\u2019s designated CareersPOV coordinator.',
                    'The school coordinator will review the matter and coordinate with CareersPOV where technical assistance is necessary.',
                  ]} />

                  <h3 className="text-sm sm:text-base font-semibold text-[#1F2937] font-['Outfit'] mt-6 mb-2">Platform Privacy and Security Enquiries</h3>
                  <p className="text-sm sm:text-[15px] text-[#4B5563] leading-[1.8] mb-3">For matters specifically relating to the CareersPOV platform, technical security incidents, unauthorized platform access, or personal information processed independently by Giggly Gains, contact:</p>
                  <ContactBlock />
                  <Paragraphs items={[
                    'Individuals may also contact Giggly Gains directly to exercise rights available under applicable data protection law.',
                    'Please provide sufficient information to identify the relevant account or concern.',
                    'Do not send passwords, unnecessary identity documents or sensitive student information through ordinary email.',
                    'Nothing in this Privacy Policy limits rights or remedies available under applicable law.',
                  ]} />
                </>
              )}
            </section>
          ))}
        </div>

        {/* Back to top */}
        <div className="mt-12 pt-6 border-t border-gray-200 text-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="text-sm text-[#9CA3AF] hover:text-[#6B7280] transition-colors"
          >
            Back to top
          </button>
        </div>
      </div>
    </div>
  );
}
